import { useCallback, useEffect, useRef, useState } from 'react';
import { Copy } from 'lucide-react';
import { Button } from './Button';

/** Copy control with the spec's transient "Copied!" tooltip (see the "Create a new client" section), fading after ~2 s. */
export function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Clipboard permission denied — the value stays selectable on screen.
    }
    setCopied(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 2000);
  }, [value]);

  return (
    <span className="relative inline-flex items-center">
      <Button
        size="sm"
        onClick={() => void copy()}
        aria-label={label}
        icon={<Copy size={12} aria-hidden="true" />}
      >
        Copy
      </Button>
      {copied ? (
        <span
          role="status"
          className="absolute right-0 top-full mt-1 whitespace-nowrap rounded border border-hairline-strong bg-surface px-1.5 py-0.5 text-2xs text-ink shadow-popover"
        >
          Copied!
        </span>
      ) : null}
    </span>
  );
}
